// Edytor montazu: wybor klocka z katalogu, podglad, przyciaganie do wolnych
// mocowan, obrot i usuwanie. Operuje na magazynie konstrukcji i kamerze.
// Nie zna Plancka ani szczegolow rysowania.

import { getDefinition } from '../domain/catalog';
import { footprintCenter, partBoundsGrid, rotateVec } from '../domain/geometry';
import type { PartInstance, PartDefinition, Rotation, Connection } from '../domain/types';
import type { BlueprintStore } from '../app/store';
import { metersToGrid } from '../physics/units';
import type { Camera } from '../render/camera';
import type { PreviewGhost, PreviewSocket } from '../render/drawPreview';
import {
  candidateConnection,
  findMovePlacements,
  findPlacements,
  freeSockets,
  freeSocketsForMove,
  nearestCandidate,
  nearestCandidateByCenter,
  type PlacementCandidate,
  type SocketSlot,
} from './snapping';

/** Prog przyciagania wyrazony w pikselach ekranu (nie w kratkach). */
const SNAP_PX = 56;
const ROTATION_STEP = 90;

export type EditorMessageKind = 'info' | 'error';

export interface EditorPreview {
  placingTypeId: string | null;
  slots: readonly SocketSlot[];
  candidates: readonly PlacementCandidate[];
  hover: PlacementCandidate | null;
}

export class Editor {
  private placingTypeId: string | null = null;
  private manualRotation: Rotation | null = null;
  private candidates: PlacementCandidate[] = [];
  private slots: SocketSlot[] = [];
  private hover: PlacementCandidate | null = null;
  private pointer: { x: number; y: number } | null = null;
  /** Pusty warsztat: pozwalamy postawic pierwszy klocek swobodnie (bez mocowania). */
  private allowRoot = false;
  /** Przeciagany klocek (przenoszenie po plotnie) albo null. */
  private draggingPartId: string | null = null;
  /** Wymuszony obrot przeciaganego klocka (null = dopasuj do mocowania). */
  private dragRotation: Rotation | null = null;

  constructor(
    private readonly store: BlueprintStore,
    private readonly camera: Camera,
    private readonly onMessage: (message: string, kind: EditorMessageKind) => void,
  ) {}

  get preview(): EditorPreview {
    return {
      placingTypeId: this.placingTypeId,
      slots: this.slots,
      candidates: this.candidates,
      hover: this.hover,
    };
  }

  get isPlacing(): boolean {
    return this.placingTypeId !== null;
  }

  /** Typ klocka w trybie montazu (albo null) - do synchronizacji palety. */
  get placing(): string | null {
    return this.placingTypeId;
  }

  get isDragging(): boolean {
    return this.draggingPartId !== null;
  }

  /** Id przeciaganego klocka (do kosza i ukrycia oryginalu w rysowaniu). */
  get draggingPart(): string | null {
    return this.draggingPartId;
  }

  get rotation(): Rotation {
    return this.manualRotation ?? this.hover?.rotation ?? 0;
  }

  private toGrid(screenX: number, screenY: number): { x: number; y: number } {
    const world = this.camera.screenToWorld(screenX, screenY);
    return { x: metersToGrid(world.x), y: metersToGrid(world.y) };
  }

  private snapDistanceGrid(): number {
    return metersToGrid(SNAP_PX * this.camera.metersPerPixel);
  }

  /** Wybor klocka z katalogu wchodzi w tryb montazu (ponowny wybor go wylacza). */
  beginPlacement(typeId: string): void {
    if (this.placingTypeId === typeId) {
      this.cancelPlacement();
      this.onMessage('Anulowano montaz.', 'info');
      return;
    }
    this.placingTypeId = typeId;
    this.manualRotation = null;
    this.refresh();
    this.onMessage(`Montaz: ${getDefinition(typeId).label}. Wskaz wolne mocowanie.`, 'info');
  }

  cancelPlacement(): void {
    this.placingTypeId = null;
    this.manualRotation = null;
    this.hover = null;
    this.candidates = [];
    this.slots = [];
    this.allowRoot = false;
  }

  /** Recznie wybrany obrot podgladu (cyklicznie 0/90/180/270). */
  cycleRotation(): void {
    if (this.draggingPartId) {
      const current = this.dragRotation ?? this.hover?.rotation ?? this.dragPartRotation();
      this.dragRotation = ((current + ROTATION_STEP) % 360) as Rotation;
      this.refreshDrag();
      return;
    }
    if (!this.placingTypeId) return;
    const current = this.manualRotation ?? this.hover?.rotation ?? 0;
    this.manualRotation = ((current + ROTATION_STEP) % 360) as Rotation;
    this.refresh();
  }

  /** Przeliczenie kandydatow po zmianie konstrukcji (dodanie, usuniecie, cofniecie). */
  refresh(): void {
    if (this.draggingPartId) {
      this.refreshDrag();
      return;
    }
    if (!this.placingTypeId) {
      this.slots = [];
      this.candidates = [];
      this.hover = null;
      this.allowRoot = false;
      return;
    }
    const blueprint = this.store.current;
    this.slots = freeSockets(blueprint);
    this.candidates = findPlacements(
      blueprint,
      this.placingTypeId,
      this.manualRotation === null
        ? {}
        : { forceRotation: true, rotation: this.manualRotation },
    );
    // Bez zadnego klocka nie ma mocowan - pierwszy stawiamy swobodnie.
    this.allowRoot = blueprint.parts.length === 0;
    this.updateHover();
  }

  /** Przeliczenie kandydatow dla PRZENOSZONEGO klocka (przyciaganie do mocowan). */
  private refreshDrag(): void {
    const partId = this.draggingPartId;
    if (!partId) return;
    const blueprint = this.store.current;
    const part = blueprint.parts.find((entry) => entry.id === partId);
    if (!part) {
      this.cancelDrag();
      return;
    }
    this.slots = freeSocketsForMove(blueprint, partId);
    this.candidates = findMovePlacements(
      blueprint,
      partId,
      this.dragRotation === null ? {} : { forceRotation: true, rotation: this.dragRotation },
    );
    // Samotny klocek nie ma sie do czego przyczepic - pozwalamy go swobodnie przesunac.
    this.allowRoot = blueprint.parts.length === 1;
    this.updateHover();
  }

  /** Obrot przeciaganego klocka do podgladu: wymuszony albo biezacy klocka. */
  private dragPartRotation(): Rotation {
    if (this.dragRotation !== null) return this.dragRotation;
    const part = this.draggingPartId
      ? this.store.current.parts.find((entry) => entry.id === this.draggingPartId)
      : undefined;
    return part?.rotation ?? 0;
  }

  private updateHover(): void {
    if (!this.pointer || this.candidates.length === 0) {
      this.hover = null;
      return;
    }
    const point = this.toGrid(this.pointer.x, this.pointer.y);

    // Przenoszenie: pod palcem jest SRODEK klocka, a kandydat moze miec inny
    // obrot niz biezacy - dlatego mierzymy odleglosc do srodka kandydata.
    if (this.draggingPartId) {
      const definition = this.ghostDefinition();
      this.hover = definition
        ? nearestCandidateByCenter(this.candidates, definition, point, this.snapDistanceGrid())
        : null;
      return;
    }

    this.hover = nearestCandidate(this.candidates, point, this.snapDistanceGrid());
  }

  /**
   * Origin, ktory przy biezacym wskazniku przyjmie stawiany albo swobodnie
   * przeciagany klocek (jednostki siatki) - do podgladu "ducha" poza mocowaniem.
   * Przy przeciaganiu pod palcem jest SRODEK klocka, wiec odejmujemy polowe
   * obrysu. Przyciaganie (trafienia w mocowania) liczy sie osobno, po srodku
   * kandydata (patrz updateHover).
   */
  private originGridUnderPointer(): { x: number; y: number } {
    const pointer = this.pointer;
    if (!pointer) return { x: 0, y: 0 };
    const point = this.toGrid(pointer.x, pointer.y);
    const definition = this.ghostDefinition();
    if (this.draggingPartId && definition) {
      const center = rotateVec(footprintCenter(definition), this.dragPartRotation());
      return { x: point.x - center.x, y: point.y - center.y };
    }
    return point;
  }

  setPointer(screenX: number, screenY: number): void {
    this.pointer = { x: screenX, y: screenY };
    this.updateHover();
  }

  clearPointer(): void {
    this.pointer = null;
    this.hover = null;
  }

  /** Zatwierdzenie montazu w biezacym miejscu wskaznika. */
  confirm(): boolean {
    if (!this.placingTypeId) return false;

    if (!this.hover) {
      // Pusty warsztat: pierwszy klocek stawiamy swobodnie pod wskaznikiem.
      if (this.allowRoot && this.pointer) {
        const point = this.toGrid(this.pointer.x, this.pointer.y);
        const instance: PartInstance = {
          id: this.nextPartId(),
          typeId: this.placingTypeId,
          gridX: Math.round(point.x),
          gridY: Math.round(point.y),
          rotation: this.manualRotation ?? 0,
        };
        if (!this.place(instance, null)) return false;
        // Montujemy jedna instancje na raz - tryb montazu konczy sie po postawieniu,
        // zeby kolejne dotkniecie moglo juz chwycic i przesuwac ten klocek.
        this.cancelPlacement();
        this.refresh();
        this.onMessage(`Dodano pierwszy klocek: ${getDefinition(instance.typeId).label}.`, 'info');
        return true;
      }
      this.onMessage('Brak zgodnego mocowania w tym miejscu.', 'error');
      return false;
    }
    if (!this.hover.valid) {
      this.onMessage(this.hover.reason ?? 'Nie mozna tu postawic tego klocka.', 'error');
      return false;
    }

    const instance: PartInstance = {
      id: this.nextPartId(),
      typeId: this.placingTypeId,
      gridX: this.hover.gridX,
      gridY: this.hover.gridY,
      rotation: this.hover.rotation,
    };
    if (!this.place(instance, candidateConnection(this.hover, instance.id))) return false;

    const label = getDefinition(instance.typeId).label;
    // Koniec montazu po jednej instancji (patrz wyzej).
    this.cancelPlacement();
    this.refresh();
    this.onMessage(`Dodano: ${label}.`, 'info');
    return true;
  }

  /**
   * Wstawia klocek do konstrukcji. Gdy zabraknie budzetu albo wyczerpie sie
   * limit klockow tej trasy, pokazuje powod i NIE zmienia konstrukcji (zwraca
   * false) - tryb montazu zostaje, wiec mozna wybrac inny klocek albo anulowac.
   */
  private place(instance: PartInstance, connection: Connection | null): boolean {
    if (this.store.addPart(instance, connection)) return true;

    const limit = this.store.remainingForType(instance.typeId);
    if (limit !== undefined && limit <= 0) {
      this.onMessage('Limit tej trasy: takich klockow nie mozna dodac wiecej.', 'error');
      return false;
    }

    const remaining = this.store.remainingBudget;
    this.onMessage(
      remaining === undefined
        ? 'Nie mozna dodac tego klocka.'
        : `Brak budzetu: zostalo ${remaining}. Usun klocek albo wybierz tanszy.`,
      'error',
    );
    return false;
  }

  /** Klocek pod punktem ekranu; kola maja pierwszenstwo (rysowane z przodu). */
  private hitTest(screenX: number, screenY: number): PartInstance | null {
    const world = this.camera.screenToWorld(screenX, screenY);
    const point = { x: metersToGrid(world.x), y: metersToGrid(world.y) };

    const ordered = [...this.store.current.parts].sort((left, right) => {
      const rank = (part: PartInstance) =>
        getDefinition(part.typeId).category === 'wheel' ? 0 : 1;
      return rank(left) - rank(right);
    });

    for (const part of ordered) {
      const bounds = partBoundsGrid(part, getDefinition(part.typeId));
      if (
        point.x >= bounds.minX &&
        point.x <= bounds.maxX &&
        point.y >= bounds.minY &&
        point.y <= bounds.maxY
      ) {
        return part;
      }
    }
    return null;
  }

  /**
   * Dotkniecie lub klik obszaru gry. W trybie montazu zatwierdza klocek,
   * poza nim zaznacza klocek pod palcem - na dotyku to podstawowy sposob pracy
   * (bez zaleznosci od precyzyjnego przeciagania).
   */
  handleTap(screenX: number, screenY: number): void {
    this.pointer = { x: screenX, y: screenY };
    if (this.placingTypeId) {
      this.updateHover();
      this.confirm();
      return;
    }
    const hit = this.hitTest(screenX, screenY);
    this.store.select(hit ? hit.id : null);
  }

  /** Klocek pod punktem ekranu albo null - do rozpoczecia przeciagania. */
  partAt(screenX: number, screenY: number): string | null {
    return this.hitTest(screenX, screenY)?.id ?? null;
  }

  /**
   * Rozpoczecie przeciagania istniejacego klocka. Wskazujemy klocek po id
   * (ustalonym przy pointerdown), a biezacy wskaznik wyznacza podglad.
   * Zwraca false, gdy klocka nie ma albo trwa montaz nowego.
   */
  beginDrag(partId: string, screenX: number, screenY: number): boolean {
    if (this.placingTypeId) return false;
    const part = this.store.current.parts.find((entry) => entry.id === partId);
    if (!part) return false;

    this.draggingPartId = partId;
    // Domyslnie obrot jest DOPASOWYWANY do mocowania (jak przy montazu);
    // gracz moze go wymusic przyciskiem Obroc.
    this.dragRotation = null;
    this.pointer = { x: screenX, y: screenY };
    this.store.select(partId);
    this.refreshDrag();
    return true;
  }

  /** Ruch wskaznika w trakcie przeciagania (podglad na biezaco). */
  updateDrag(screenX: number, screenY: number): void {
    if (!this.draggingPartId) return;
    this.pointer = { x: screenX, y: screenY };
    this.updateHover();
  }

  /** Przerwanie przeciagania bez zmiany konstrukcji (np. anulowanie gestu). */
  cancelDrag(): void {
    this.draggingPartId = null;
    this.dragRotation = null;
    this.hover = null;
    this.candidates = [];
    this.slots = [];
    this.allowRoot = false;
  }

  /**
   * Upuszczenie przeciaganego klocka. Trafienie w mocowanie przenosi klocek
   * (przyciagniecie jak przy montazu), a samotny klocek na pustym warsztacie
   * mozna przesunac swobodnie. W innym wypadku konstrukcja zostaje bez zmian.
   */
  confirmDrag(): boolean {
    const partId = this.draggingPartId;
    if (!partId) return false;

    const part = this.store.current.parts.find((entry) => entry.id === partId);
    const name = part ? getDefinition(part.typeId).label : 'klocek';

    if (this.hover?.valid) {
      // Upuszczenie na tym samym miejscu to brak zmiany - nie zasmiecamy
      // historii ani nie pokazujemy bledu.
      const sameSpot =
        part !== undefined &&
        part.gridX === this.hover.gridX &&
        part.gridY === this.hover.gridY &&
        part.rotation === this.hover.rotation;
      if (sameSpot) {
        this.finishDrag();
        return false;
      }

      const moved = this.store.movePart(partId, {
        gridX: this.hover.gridX,
        gridY: this.hover.gridY,
        rotation: this.hover.rotation,
      });
      this.finishDrag();
      this.onMessage(
        moved ? `Przeniesiono: ${name}.` : 'Nie mozna tu przeniesc tego klocka.',
        moved ? 'info' : 'error',
      );
      return moved;
    }

    // Swobodny ruch samotnego klocka (bez mocowania).
    if (this.allowRoot && this.pointer) {
      const origin = this.originGridUnderPointer();
      const moved = this.store.movePart(
        partId,
        {
          gridX: Math.round(origin.x),
          gridY: Math.round(origin.y),
          rotation: this.dragPartRotation(),
        },
        { allowFree: true },
      );
      this.finishDrag();
      this.onMessage(moved ? `Przeniesiono: ${name}.` : 'Klocek wraca na miejsce.', 'info');
      return moved;
    }

    this.onMessage(this.hover?.reason ?? 'Upusc klocek na wolnym mocowaniu.', 'error');
    this.finishDrag();
    return false;
  }

  private finishDrag(): void {
    this.cancelDrag();
    this.refresh();
  }

  /** Usuniecie klocka po upuszczeniu na kosz; zwraca true, gdy cos usunieto. */
  deletePart(partId: string): boolean {
    const part = this.store.current.parts.find((entry) => entry.id === partId);
    if (!part) return false;
    this.store.removePart(partId);
    this.onMessage(`Usunieto: ${getDefinition(part.typeId).label}.`, 'info');
    return true;
  }

  deleteSelected(): boolean {
    const partId = this.store.selectedPartId;
    if (!partId) {
      this.onMessage('Najpierw wybierz klocek.', 'error');
      return false;
    }
    const part = this.store.current.parts.find((entry) => entry.id === partId);
    this.store.removePart(partId);
    this.refresh();
    this.onMessage(`Usunieto: ${part ? getDefinition(part.typeId).label : 'klocek'}.`, 'info');
    return true;
  }

  /**
   * Obrot zaznaczonego klocka. Polaczenia sa geometryczne, wiec obrot jest
   * przyjmowany tylko wtedy, gdy klocek nadal da sie podlaczyc.
   */
  rotateSelected(): boolean {
    const partId = this.store.selectedPartId;
    if (!partId) {
      this.onMessage('Najpierw wybierz klocek.', 'error');
      return false;
    }
    const part = this.store.current.parts.find((entry) => entry.id === partId);
    if (!part) return false;

    const next = ((part.rotation + ROTATION_STEP) % 360) as Rotation;
    if (!this.store.rotatePart(partId, next)) {
      this.onMessage('Obroc tego klocka rozspojnilby konstrukcje.', 'error');
      return false;
    }
    this.onMessage('Obrocono klocek.', 'info');
    return true;
  }

  private nextPartId(): string {
    const parts = this.store.current.parts;
    let index = parts.length + 1;
    let id = `part_${index}`;
    while (parts.some((part) => part.id === id)) {
      index += 1;
      id = `part_${index}`;
    }
    return id;
  }

  /** Mocowania do rysowania: zielone te, na ktorych da sie postawic wybrany klocek. */
  previewSockets(): PreviewSocket[] {
    const compatible = new Set(
      this.candidates
        .filter((candidate) => candidate.valid)
        .map((candidate) => `${candidate.slot.partId}#${candidate.slot.socketId}`),
    );
    return this.slots.map((slot) => ({
      grid: slot.grid,
      kind: slot.kind,
      normal: slot.normal,
      compatible: compatible.has(`${slot.partId}#${slot.socketId}`),
    }));
  }

  /**
   * "Duch" klocka do narysowania. Gdy jest dopasowanie - dokladnie na siatce
   * w miejscu montazu; gdy nie ma - pod wskaznikiem (montaz na czerwono,
   * przenoszenie tak, by srodek klocka byl pod palcem).
   */
  ghost(): PreviewGhost | null {
    const definition = this.ghostDefinition();
    if (!definition) return null;

    if (this.hover) {
      return {
        definition,
        gridX: this.hover.gridX,
        gridY: this.hover.gridY,
        rotation: this.hover.rotation,
        valid: this.hover.valid,
      };
    }
    if (!this.pointer) return null;

    const origin = this.originGridUnderPointer();
    if (this.draggingPartId) {
      return {
        definition,
        gridX: Math.round(origin.x),
        gridY: Math.round(origin.y),
        rotation: this.dragPartRotation(),
        // Przeciagany klocek musi trafic w mocowanie - poza nim podglad jest czerwony.
        valid: false,
      };
    }

    return {
      definition,
      gridX: Math.round(origin.x),
      gridY: Math.round(origin.y),
      rotation: this.manualRotation ?? 0,
      // Pierwszy klocek na pustym warsztacie jest poprawny wszedzie.
      valid: this.allowRoot,
    };
  }

  /** Definicja klocka rysowanego jako duch: montowanego albo przeciaganego. */
  private ghostDefinition(): PartDefinition | null {
    if (this.draggingPartId) {
      const part = this.store.current.parts.find((entry) => entry.id === this.draggingPartId);
      return part ? getDefinition(part.typeId) : null;
    }
    return this.placingTypeId ? getDefinition(this.placingTypeId) : null;
  }
}